/**
 * Stream Schedule — v2 port of v1's stream-schedule.ejs + StreamSchedule model.
 * Renders a list of upcoming streams from configuration.
 *
 * Config:
 *   schedule: ScheduleEntry[]      (required)
 *   accentColor: string            (default '#a78bfa')
 *   title: string                  (default 'Upcoming Streams')
 *   maxRows: number                (default 4)
 *   timezone: IANA tz string       (default browser local — affects formatting only)
 *
 * ScheduleEntry: { id, title, startsAt: ISO, durationMinutes?, category?, notes? }
 */

interface ScheduleEntry {
  id: string;
  title: string;
  startsAt: string;
  durationMinutes?: number;
  category?: string;
  notes?: string;
}

function fmtDay(d: Date): string {
  const today = new Date();
  const tomorrow = new Date(today.getTime() + 86400_000);
  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, tomorrow)) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function StreamScheduleWidget({ fields }: { fields: Record<string, unknown> }) {
  const accent = String(fields.accentColor ?? '#a78bfa');
  const title = String(fields.title ?? 'Upcoming Streams');
  const maxRows = Number(fields.maxRows ?? 4);
  const schedule = (fields.schedule as ScheduleEntry[] | undefined) ?? [];
  const now = Date.now();
  const upcoming = schedule
    .map((s) => ({ ...s, startsAtMs: new Date(s.startsAt).getTime() }))
    .filter((s) => !isNaN(s.startsAtMs) && s.startsAtMs > now - 60 * 60_000)
    .sort((a, b) => a.startsAtMs - b.startsAtMs)
    .slice(0, maxRows);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 14,
        padding: '14px 18px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 320,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          right: 22,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ color: accent }}>📅</span>
        {title}
      </div>
      {upcoming.length === 0 ? (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>No upcoming streams</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {upcoming.map((s) => {
            const d = new Date(s.startsAtMs);
            const isLive = s.startsAtMs <= now;
            return (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: isLive ? `${accent}1f` : 'rgba(255,255,255,0.02)',
                  border: isLive ? `1px solid ${accent}66` : '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flexShrink: 0,
                    minWidth: 64,
                  }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: 1,
                      textTransform: 'uppercase',
                      color: accent,
                    }}
                  >
                    {isLive ? 'LIVE' : fmtDay(d)}
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                      marginTop: 2,
                    }}
                  >
                    {fmtTime(d)}
                  </span>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.title}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: 'rgba(255,255,255,0.55)',
                      marginTop: 2,
                      display: 'flex',
                      gap: 8,
                    }}
                  >
                    {s.category && (
                      <span
                        style={{
                          fontWeight: 700,
                          color: accent,
                          letterSpacing: 0.5,
                          textTransform: 'uppercase',
                          fontSize: 9,
                        }}
                      >
                        {s.category}
                      </span>
                    )}
                    {s.durationMinutes && <span>~{s.durationMinutes} min</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
